/**
 * mapTravelUrl — the address a cross-map trip lands on.
 *
 * One builder for both ways of travelling: the full-page navigation
 * (SceneEventManager.navigateToMap → location.assign) and the in-place trip
 * (SeamlessTravel, plan 25). Both must produce the same URL so a refresh after
 * an in-place trip boots exactly like the reload it replaced.
 *
 * `/?map=X&preset=live-lobby` boots that map directly (BootService reads
 * ?map=, picker skipped), bypassing the /adventure→tidewend coercion.
 */

/**
 * Session/debug posture carried across a map change. Dropping freezePopup=0
 * re-enabled the client-freeze modal after every portal during QA; the dev
 * gameplay overrides (noclip, speed, godMode) are carried so fast traversal
 * testing does not reset after every portal; seamlessPortals keeps the D36
 * flag on through a fallback reload; worldFog keeps a World Map fog test on
 * (worldMapFogPolicy.ts).
 */
export const CARRIED_TRAVEL_PARAMS = [
  'freezePopup',
  'peaceful',
  'renderer',
  'testhooks',
  'teleporterDebug',
  'framejitter',
  'noclip',
  'speed',
  'godMode',
  'seamlessPortals',
  'worldFog',
] as const;

export interface MapTravelTarget {
  mapId: string;
  spawnId?: string;
  /** Opaque server-issued world-transfer receipt. */
  transferReceipt?: string;
}

/** Path + query (`/?map=…`) for a trip from a page booted with `currentSearch`. */
export function buildMapTravelUrl(currentSearch: string, target: MapTravelTarget): string {
  const cur = new URLSearchParams(currentSearch);
  const params = new URLSearchParams();
  params.set('map', target.mapId);
  if (target.spawnId) params.set('spawn', target.spawnId);
  if (target.transferReceipt) params.set('transfer', target.transferReceipt);
  params.set('preset', 'live-lobby');
  params.set('skipSplash', '1');
  params.set('skipLogin', '1');
  // Skip the class-select bridge on the destination boot: reuse the
  // already-picked character (PreGameFlow's `helix_last_character`) instead
  // of re-prompting the picker. DJ picks class in the picker UI, not via
  // `?class=`, so the URL carry alone left the destination showing the
  // picker again — keepCharacter is what actually bypasses it.
  params.set('keepCharacter', '1');
  const cls = cur.get('class');
  if (cls) params.set('class', cls);
  for (const key of CARRIED_TRAVEL_PARAMS) {
    const value = cur.get(key);
    if (value !== null) params.set(key, value);
  }
  return `/?${params.toString()}`;
}
